import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { PButton, PText } from "@porsche-design-system/components-react"
import { useCallback, useContext } from "react"
import type { MessageDescriptor } from "react-intl"
import { FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { ButtonGroup } from "src/components"
import { Modal } from "src/context/modal/Modal"
import { ModalContext } from "src/context/modal/ModalContext"
import type { ModalProps } from "src/context/modal/ModalProvider"
import { priceConfirmationModalMessages } from "./priceConfirmationModal.messages"

export interface PriceConfirmationModalProps {
	currentPrice: number
	headline: MessageDescriptor
	label: MessageDescriptor
	onConfirmClicked: () => void
}

type Props = ModalProps<PriceConfirmationModalProps>

export const PriceConfirmationModal = ({
	id,
	modalProps: { currentPrice, headline, label, onConfirmClicked },
}: Props) => {
	const { formatMessage, formatNumber } = useIntl()
	const { hideModal } = useContext(ModalContext)
	const { currency } = useDealerContext()

	const formattedPrice = formatNumber(currentPrice, {
		currency,
		style: "currency",
		unitDisplay: "short",
	})

	const hide = useCallback(() => hideModal(id), [id, hideModal])

	const handleConfirmClicked = () => {
		hide()
		onConfirmClicked()
	}

	return (
		<Modal id={id} heading={formatMessage(headline)}>
			<ModalContent>
				<PText>
					<FormattedMessage
						{...label}
						values={{
							b: (chunks) => <b>{chunks}</b>,
							price: formattedPrice,
						}}
					/>
				</PText>

				<ButtonGroup>
					<PButton onClick={handleConfirmClicked} data-testid="modal-price-confirmation-submit">
						<FormattedMessage {...priceConfirmationModalMessages.button} />
					</PButton>
					<PButton type="button" variant="secondary" onClick={hide}>
						<FormattedMessage {...formMessages.cancel} />
					</PButton>
				</ButtonGroup>
			</ModalContent>
		</Modal>
	)
}

const ModalContent = styled.div`
	max-width: 40rem;
`
